import { useEffect, useState, useRef, useCallback } from "react";
import { useBackButton } from "@/hooks/useBackButton";
import { supabase } from "@/integrations/supabase/client";
import { playNotificationSound } from "@/utils/notificationSound";
import { Bell, X, Smartphone, MessageSquare } from "lucide-react";

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  read: boolean;
  created_at: string;
}

const NotificationCenter = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [userId, setUserId] = useState<string | null>(null);
  const [showPanel, setShowPanel] = useState(false);
  const closePanel = useCallback(() => setShowPanel(false), []);
  useBackButton(showPanel, closePanel, "notifications");
  const lastCountRef = useRef(0);
  const initialLoadRef = useRef(true);

  // Swipe state
  const [swipingId, setSwipingId] = useState<string | null>(null);
  const [swipeX, setSwipeX] = useState(0);
  const touchStartRef = useRef(0);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      setUserId(session?.user?.id ?? null);
    });
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUserId(session?.user?.id ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);

  const fetchNotifications = useCallback(async () => {
    if (!userId) return;
    const { data } = await supabase
      .from("notifications")
      .select("*")
      .eq("user_id", userId)
      .order("created_at", { ascending: false })
      .limit(20);

    if (data) {
      const unread = data.filter((n: any) => !n.read).length;
      // Play sound only when new unread notifications appear (not on first load)
      if (!initialLoadRef.current && unread > lastCountRef.current) {
        playNotificationSound();
        setShowPanel(true);
      }
      initialLoadRef.current = false;
      lastCountRef.current = unread;
      setNotifications(data as Notification[]);
    }
  }, [userId]);

  // Poll for notifications every 10s
  useEffect(() => {
    if (!userId) {
      setNotifications([]);
      return;
    }
    initialLoadRef.current = true;
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 10000);
    return () => clearInterval(interval);
  }, [userId, fetchNotifications]);

  const markAsRead = async (id: string) => {
    await supabase.from("notifications").update({ read: true }).eq("id", id);
    setNotifications((prev) => prev.map((n) => n.id === id ? { ...n, read: true } : n));
  };

  const deleteNotification = async (id: string) => {
    await supabase.from("notifications").delete().eq("id", id);
    setNotifications((prev) => prev.filter((n) => n.id !== id));
    setSwipingId(null);
    setSwipeX(0);
  };

  const handleTouchStart = (id: string, e: React.TouchEvent) => {
    touchStartRef.current = e.touches[0].clientX;
    setSwipingId(id);
    setSwipeX(0);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!swipingId) return;
    const delta = e.touches[0].clientX - touchStartRef.current;
    // Only allow left swipe
    setSwipeX(Math.min(0, delta));
  };

  const handleTouchEnd = () => {
    if (!swipingId) return;
    if (swipeX < -100) {
      deleteNotification(swipingId);
    } else {
      setSwipeX(0);
      setSwipingId(null);
    }
  };

  const unreadCount = notifications.filter((n) => !n.read).length;

  if (!userId) return null;

  const getIcon = (type: string) => {
    if (type === "suggestion_accepted") return <Smartphone className="w-5 h-5 text-primary flex-shrink-0" />;
    if (type === "ticket_replied") return <MessageSquare className="w-5 h-5 text-primary flex-shrink-0" />;
    return <Bell className="w-5 h-5 text-primary flex-shrink-0" />;
  };

  return (
    <div className="relative">
      <button
        onClick={() => {
          setShowPanel(!showPanel);
          // Mark all as read when opening
          if (!showPanel) {
            notifications.filter((n) => !n.read).forEach((n) => markAsRead(n.id));
          }
        }}
        className="relative p-2 text-foreground hover:text-primary hover:scale-110 transition-all"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-gradient-gold text-primary-foreground text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold animate-[scale-in_0.3s_ease-out]">
            {unreadCount}
          </span>
        )}
      </button>

      {showPanel && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 z-40" onClick={() => setShowPanel(false)} />

          {/* Panel */}
          <div className="fixed left-1/2 top-16 -translate-x-1/2 w-80 sm:w-96 max-h-[70vh] bg-card border border-border rounded-xl shadow-2xl z-50 overflow-hidden animate-[fade-in_0.2s_ease-out]">
            <div className="px-4 py-3 border-b border-border flex items-center justify-between">
              <h3 className="font-display text-sm font-bold text-foreground flex items-center gap-2">
                <Bell className="w-4 h-4 text-primary" />
                Notificações
              </h3>
              <button onClick={() => setShowPanel(false)} className="p-1 text-muted-foreground hover:text-foreground transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="overflow-y-auto max-h-[60vh]">
              {notifications.length === 0 ? (
                <div className="py-12 text-center">
                  <Bell className="w-10 h-10 text-muted-foreground/30 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">Nenhuma notificação</p>
                </div>
              ) : (
                <>
                  <div className="px-4 py-2 text-center">
                    <p className="text-[10px] text-muted-foreground/50">← Arraste para o lado para apagar</p>
                  </div>
                  {notifications.map((n) => (
                    <div
                      key={n.id}
                      className="relative overflow-hidden"
                      onTouchStart={(e) => handleTouchStart(n.id, e)}
                      onTouchMove={handleTouchMove}
                      onTouchEnd={handleTouchEnd}
                    >
                      {/* Delete background (revealed on swipe) */}
                      <div className="absolute inset-0 bg-muted flex items-center justify-end pr-4">
                        <span className="text-sm font-semibold text-muted-foreground">Apagar</span>
                      </div>

                      {/* Notification content */}
                      <div
                        className={`relative px-4 py-3 border-b border-border flex gap-3 items-start transition-transform ${
                          !n.read ? "bg-primary/5" : "bg-card"
                        }`}
                        style={{
                          transform: swipingId === n.id ? `translateX(${swipeX}px)` : "translateX(0)",
                          transition: swipingId === n.id ? "none" : "transform 0.3s ease",
                        }}
                      >
                        <div className="mt-0.5">{getIcon(n.type)}</div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-foreground">{n.title}</p>
                          <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{n.message}</p>
                          <p className="text-[10px] text-muted-foreground/60 mt-1">
                            {new Date(n.created_at).toLocaleString("pt-BR")}
                          </p>
                        </div>
                        <button
                          onClick={() => deleteNotification(n.id)}
                          className="p-1 text-muted-foreground hover:text-foreground transition-colors flex-shrink-0 hidden sm:block"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationCenter;
